import React, {ReactNode, useState} from 'react';
import {Pressable, PressableProps, StyleProp, StyleSheet, Text, TextStyle} from 'react-native';

import {universal} from '../../design-system/tokens';

import type {MenuAction, ResolvedHeading} from './menu/menuTypes';

export type MenuLinkTextProps = {
  /** A resolved link, or a plain-label group heading (R56). */
  link: ResolvedHeading;
  style?: StyleProp<TextStyle>;
  onAction(a: MenuAction): void;
};

export type UnderlinePressableProps = Omit<
  PressableProps,
  'children' | 'onHoverIn' | 'onHoverOut'
> & {
  /** Renders the content; `underlined` is true while hovered (web) or pressed. */
  children(underlined: boolean): ReactNode;
};

/**
 * The mega menu link hover rule (DIS-1938): underlined while hovered or pressed. MenuLinkText
 * and the account panel rows (DIS-1937) share it.
 */
export const UnderlinePressable = ({children, ...props}: UnderlinePressableProps) => {
  // Web hover is onHoverIn. Native press is Pressable's `pressed` (the children callback).
  // Both stay here: MobileMenuSheet renders MenuLinkText as well as MegaMenuPanel.
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable {...props} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)}>
      {({pressed}) => children(hovered || pressed)}
    </Pressable>
  );
};

/**
 * One mega menu link (spec § 7.3.1), ported from the header prototype (ea875fae6
 * MegaMenuPanel.tsx). The switch is exhaustive: a new status fails type-check here.
 * - `live`: a link; a press reports its action.
 * - `disabled`: greyed out, no press handler, `aria-disabled`; the reason is the hint (RP7).
 * - `label`: plain heading text (R56): not greyed, not disabled, no press.
 */
export const MenuLinkText = ({link, style, onAction}: MenuLinkTextProps) => {
  switch (link.status) {
    case 'live':
      return (
        <UnderlinePressable onPress={() => onAction(link.action)} accessibilityRole="link">
          {underlined => <Text style={[style, underlined && menuLinkUnderline]}>{link.label}</Text>}
        </UnderlinePressable>
      );
    case 'disabled':
      return (
        <Text
          style={[style, styles.disabled]}
          accessibilityRole="link"
          aria-disabled={true}
          accessibilityState={{disabled: true}}
          accessibilityHint={link.reason}>
          {link.label}
        </Text>
      );
    case 'label':
      return <Text style={style}>{link.label}</Text>;
    default: {
      const unhandled: never = link;
      return unhandled;
    }
  }
};

const styles = StyleSheet.create({
  // SHORTCUT: greyed out = universal.greyDark text, a prototype choice (RP7); no disabled-link
  // colour is designed. Ceiling: PR B (each disabled link becomes live or hidden before
  // release, DIS-1900). Upgrade path: design supplies the disabled state.
  // cursor 'auto': not pressable, so no pointer (react-native-web gives role="link" one).
  disabled: {color: universal.greyDark, cursor: 'auto'},
  underlined: {textDecorationLine: 'underline'},
});

/** The hover and press state of a menu link: the text style UnderlinePressable switches on. */
export const menuLinkUnderline = styles.underlined;
